import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { ToasterComponent, ToastTypes } from '../../components/toaster/toaster.component';

interface BootstrapToast {
  id: string;
  present(): Promise<void>;
  dismiss(): Promise<void>;
}
declare const bootstrap: {
  Toast: new (
    element: HTMLElement,
    config?: { animation?: boolean; autohide?: boolean; delay?: number }
  ) => HTMLElement & { dispose(): void; hide(): void; show(): void };
};

export interface ToastConfig {
  type: ToastTypes;
  autohide?: boolean;
  delay?: number;
  showDismiss?: boolean;
  specialContent?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  protected openedToasts: BootstrapToast[] = [];

  protected readonly environmentInj = inject(EnvironmentInjector);

  protected readonly applicationRef = inject(ApplicationRef);

  protected readonly $document = inject(DOCUMENT);

  /**
   * Opens toast of requested type
   * @param message
   * @param config
   * @returns
   */
  public openToast(message: string, config: ToastConfig): Observable<BootstrapToast> {
    const id = this.generateId();
    const toastWrapper = document.createElement('div');
    toastWrapper.id = id;
    toastWrapper.setAttribute('tabindex', '-1'); // Required for accessibility
    toastWrapper.setAttribute('aria-labelledby', 'dynamicModalLabel'); // Unique ID for accessibility
    toastWrapper.setAttribute('aria-hidden', 'true'); // Initially hidden
    toastWrapper.classList.add('toast-container', 'position-fixed', 'p-3', 'toast-position');

    const toasterInstance = createComponent(ToasterComponent, {
      environmentInjector: this.environmentInj,
      hostElement: toastWrapper,
    });

    toasterInstance.setInput('message', message);
    toasterInstance.setInput('toastType', config.type);
    toasterInstance.setInput('specialContent', config.specialContent ?? null);

    this.applicationRef.attachView(toasterInstance.hostView);

    const toasterInstanceRootNode = (toasterInstance.hostView as EmbeddedViewRef<unknown>)
      .rootNodes[0] as HTMLDivElement;

    this.$document.body.appendChild(toastWrapper);
    toasterInstance.changeDetectorRef.detectChanges();

    const toast = new bootstrap.Toast(toasterInstanceRootNode.childNodes[0] as HTMLDivElement, {
      autohide: config.autohide ?? true,
      delay: config.delay ?? 4000,
    });

    toasterInstanceRootNode.addEventListener('hidden.bs.toast', () => {
      this.applicationRef.detachView(toasterInstance.hostView);
      const modalRefIndex = this.openedToasts.findIndex((pushedRefs) => pushedRefs.id === id);
      if (modalRefIndex >= 0) {
        this.openedToasts.splice(modalRefIndex, 1);
      }
    });

    const present = async () => {
      toast.show();
      toastWrapper.removeAttribute('aria-hidden');
      this.openedToasts.push(toastRef);
    };

    const dismiss = async () => toast.hide();

    const toastRef: BootstrapToast = {
      id,
      present,
      dismiss,
    };

    return of(toastRef);
  }

  /**
   * Opens success toast
   * @param message
   * @param config
   */
  public openSuccessToast(
    message: string,
    config: Omit<ToastConfig, 'type'> = {}
  ): Observable<BootstrapToast> {
    return this.openToast(message, {
      ...config,
      type: ToastTypes.Success,
    });
  }

  /**
   * Opens error toast
   * @param message
   * @param config
   * @returns
   */
  public openErrorToast(
    message: string,
    config: Omit<ToastConfig, 'type'> = {}
  ): Observable<BootstrapToast> {
    return this.openToast(message, {
      ...config,
      type: ToastTypes.Error,
    });
  }

  public generateId(): string {
    // need to move it to central area for re-usability
    return crypto.randomUUID();
  }
}
